import { useEffect, useRef, useState, type RefObject } from "react";

const STICKY_ACTION_BAR_ROOT_MARGIN = "0px 0px -96px 0px";

export function useInPageActionVisibility<T extends HTMLElement>(
  key: string | number | null | undefined,
  onlyAfterPassed = false,
): { actionRef: RefObject<T>; isVisible: boolean } {
  const actionRef = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (key == null) return;
    setIsVisible(true);
    const target = actionRef.current;
    if (!target) return;
    if (typeof IntersectionObserver === "undefined") {
      if (onlyAfterPassed) {
        const update = () => setIsVisible(target.getBoundingClientRect().bottom > 0);
        update();
        window.addEventListener("scroll", update, { passive: true });
        return () => window.removeEventListener("scroll", update);
      }
      setIsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting || (onlyAfterPassed && entry.boundingClientRect.bottom > 0)),
      // Observe the exit boundary in passed-only mode: a halfway notification
      // can leave the bar hidden throughout a slow scroll past the action.
      { threshold: onlyAfterPassed ? 0 : 0.5, rootMargin: STICKY_ACTION_BAR_ROOT_MARGIN },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [key, onlyAfterPassed]);

  return { actionRef, isVisible };
}
